import type { ShapeStyle, ConnectorStyle, TextStyle, Point } from './types';
import { Shape } from '../shapes/Shape';
import { Connector } from '../shapes/Connector';
import { TextElement } from '../shapes/TextElement';
import type { ShapeRecognizer } from '../tools/ShapeRecognizer';

export interface DiagramSerializedElements {
  shapes: Record<string, unknown>[];
  connectors: Record<string, unknown>[];
  textElements: Record<string, unknown>[];
}

export interface DiagramDeserializedElements {
  shapes: Shape[];
  connectors: Connector[];
  textElements: TextElement[];
}

export const serializeDiagramElements = (
  shapes: Shape[],
  connectors: Connector[],
  textElements: TextElement[]
): DiagramSerializedElements => ({
  shapes: shapes.map((shape) => shape.toJSON() as Record<string, unknown>),
  connectors: connectors.map((connector) => connector.toJSON() as Record<string, unknown>),
  textElements: textElements.map((textElement) => textElement.toJSON() as Record<string, unknown>),
});

export const serializeDiagramElementsJson = (
  shapes: Shape[],
  connectors: Connector[],
  textElements: TextElement[]
): string => JSON.stringify(serializeDiagramElements(shapes, connectors, textElements));

export const deserializeDiagramElements = (
  data: DiagramSerializedElements,
  recognizer: ShapeRecognizer
): DiagramDeserializedElements => {
  const shapeIdMap = new Map<string, string>();
  const shapes: Shape[] = [];

  for (const item of data.shapes) {
    const shapeType = item['type'] as string;
    const position = item['position'] as Point;
    const size = item['size'] as { width: number; height: number };
    const style = item['style'] as Partial<ShapeStyle>;
    const text = (item['text'] as string) ?? '';
    const textStyle = item['textStyle'] as TextStyle | undefined;
    const oldId = item['id'] as string | undefined;

    const shape = recognizer.createShapeAtPosition(
      shapeType as 'rectangle' | 'ellipse' | 'diamond' | 'cylinder' | 'triangle',
      { x: position.x + size.width / 2, y: position.y + size.height / 2 },
      size,
      style
    );
    shape.text = text;
    if (textStyle) {
      shape.textStyle = { ...shape.textStyle, ...textStyle };
    }

    if (oldId) {
      shapeIdMap.set(oldId, shape.id);
    }

    shapes.push(shape);
  }

  const connectors: Connector[] = [];
  for (const item of data.connectors) {
    const startPoint = item['startPoint'] as Point;
    const endPoint = item['endPoint'] as Point;
    const style = item['style'] as Partial<ConnectorStyle>;
    const text = (item['text'] as string) ?? '';

    const connector = new Connector(startPoint, endPoint, style);
    connector.text = text;
    connector.textSize = (item['textSize'] as number) ?? 12;
    connector.arcPoint = (item['arcPoint'] as Point) ?? null;

    const oldStartShapeId = (item['startShapeId'] as string) ?? null;
    const oldEndShapeId = (item['endShapeId'] as string) ?? null;

    connector.startShapeId = oldStartShapeId ? (shapeIdMap.get(oldStartShapeId) ?? null) : null;
    connector.endShapeId = oldEndShapeId ? (shapeIdMap.get(oldEndShapeId) ?? null) : null;

    // Remap connection point IDs to use new shape IDs
    const oldStartConnectionPointId = (item['startConnectionPointId'] as string) ?? null;
    const oldEndConnectionPointId = (item['endConnectionPointId'] as string) ?? null;

    connector.startConnectionPointId = oldStartConnectionPointId && oldStartShapeId && connector.startShapeId
      ? oldStartConnectionPointId.replace(oldStartShapeId, connector.startShapeId)
      : oldStartConnectionPointId;

    connector.endConnectionPointId = oldEndConnectionPointId && oldEndShapeId && connector.endShapeId
      ? oldEndConnectionPointId.replace(oldEndShapeId, connector.endShapeId)
      : oldEndConnectionPointId;

    connectors.push(connector);
  }

  const textElements: TextElement[] = [];
  for (const item of data.textElements) {
    const position = item['position'] as Point;
    const text = (item['text'] as string) ?? '';
    const textStyle = item['textStyle'] as TextStyle;

    const textElement = new TextElement(position, text, textStyle);

    const oldId = item['id'] as string | undefined;
    if (oldId) {
      (textElement as { id: string }).id = oldId;
    }

    const maxWidth = item['maxWidth'] as number | undefined;
    if (maxWidth) {
      textElement.maxWidth = maxWidth;
    }

    const autoResize = item['autoResize'] as boolean | undefined;
    if (autoResize === false) {
      textElement.autoResize = false;
    }

    textElements.push(textElement);
  }

  return { shapes, connectors, textElements };
};
